<template>
  <layout-envelope
    :style="{ background: isMobileScreen ? 'white' : background }"
    class="text-gray-600"
  >
    <top-small class="text-chplus-regular text-interaction-dark">
      <template v-slot:left>
        <back-button
          style="color:var(--interaction-dark)"
          returnPath="election-home"
        />
      </template>
      <template v-slot:default>
        <navbar class="px-8" />
      </template>
    </top-small>
    <top-sub-b class="relative">
      <navigation-title-box
        @beforeelectionchange="onBeforeElectionChange"
        @electionchange="onElectionChanged"
      >
        <template v-slot:info>
          <div class="flex flex-row justify-center">
            <span
              v-if="!changingElection"
              class="text-chplus-s text-color-regular whitespace-no-wrap"
              v-html="$t('views.main.dream_teams.title')"
            ></span>
          </div>
        </template>
      </navigation-title-box>
      <tutorial-button class="text-interaction-dark" :show-bus="tutorialBus" />
    </top-sub-b>
    <box-filter>
      <!-- Switch between Dream Teams / Results -->
      <button-group>
        <button class="button-large active text-chplus-s">
          {{ $t("views.main.dream_teams.label") }}
        </button>
        <router-link
          class="button-large text-chplus-s"
          :to="{ name: 'results' }"
        >
          <button class="w-full h-full leading-tight">
            {{ $t("views.main.results.label") }}
          </button>
          <icon-notification
            v-if="!hasSeenResults"
            top="0"
            right="0"
            transform="translate(50%,-50%)"
          />
        </router-link>
      </button-group>
    </box-filter>
    <content-scroll-container>
      <content-scroll-inner>
        <!-- Dream Team -->
        <template v-if="choice > 0 && !changingElection">
          <div class="mb-4">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <candidate-list
                :list-id="choice"
                :showLaurel="false"
                :showChooseButton="true"
                :showViewButton="false"
                :showElectionStatus="true"
                :show-edit-button="true"
                :choose-confirmation-bus="chooseConfirmBus"
                :expanded="true"
                :allow-collapse="false"
                class="text-chplus-regular text-interaction-dark lg:col-start-2"
              />
            </div>
          </div>
          <!-- Voting Results Info -->
          <div
            v-if="new Date(this.activeElection.nextElectionDate) > new Date()"
            class="text-chplus-regular bg-interaction-light rounded-lg p-2 mb-4"
          >
            <p class="text-chplus-s mb-2">
              {{ $t("views.main.dream_teams.results") }}
            </p>
            <p
              class="text-chplus-s mb-2 font-bold"
              v-text="
                $tc(
                  'views.main.dream_teams.deadline',
                  activeElection.currentRound,
                  {
                    date: formatDate(activeElection.nextElectionDate)
                  }
                )
              "
            ></p>
          </div>
        </template>

        <template v-else-if="!changingElection">
          <div class="mb-2">
            <list-placeholder :show-list-tools="true" />
          </div>
          <div
            class="info-box text-chplus-regular bg-interaction-light rounded-lg p-2 mb-3 flex flex-col justify-center"
          >
            <p class="font-bold mb-6">
              {{ $t("views.main.dream_teams.choose_call") }}
            </p>
            <p class="mb-6">
              {{ $t("views.main.dream_teams.meet_candidates") }}
            </p>
            <p
              class="mb-6"
              v-html="
                $t('views.main.dream_teams.choose_instruction', {
                  icon:
                    '<img src=\'' +
                    require('@/assets/icons/genicon-icons8-heart-filled.svg') +
                    '\' alt=\'heart\' class=\'svg-icon align-baseline\'>'
                })
              "
            ></p>
          </div>
        </template>
        <router-link
          v-if="!changingElection"
          :to="{ name: 'voting-instructions' }"
          class="mb-4"
        >
          <navigate-big-button>
            <template v-slot:icon>
              <icon-voting-instructions />
            </template>
            <template v-slot:label>{{
              $t("views.main.dream_teams.voting_instructions")
            }}</template>
          </navigate-big-button>
        </router-link>
        <div class="h-16" v-if="!hasSeenDreamTeams"></div>
      </content-scroll-inner>
    </content-scroll-container>
    <list-choose-confirmation
      :show="showChooseConfirmation"
      :chooseNew="choosingNewList"
      :callback="chooseConfirmCallback"
    />
    <tutorial :show-bus="tutorialBus" />
  </layout-envelope>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import store from "@/store";

import LayoutEnvelope from "@/components/layouts/LayoutEnvelope";
import Navbar from "@/components/nav/NavbarMain";
import CandidateList from "@/components/list/List";
import ContentScrollContainer from "@/components/layouts/ContentScrollContainer";
import ListChooseConfirmation from "@/components/list/ListChooseConfirmation";
import Vue from "vue";
import Tutorial from "../../components/tutorials/TutorialDreamTeams";
import ButtonGroup from "../../components/buttons/ButtonGroup";
import ContentScrollInner from "@/components/layouts/ContentScrollInner";
import TopSmall from "@/components/layouts/TopSmall";
import BackButton from "@/components/buttons/BackButton";
import TopSubB from "@/components/layouts/TopSubB";
import NavigationTitleBox from "@/components/nav/NavigationTitleBox";
import TutorialButton from "@/components/buttons/TutorialButton";
import BoxFilter from "@/components/layouts/BoxFilter";
import NavigateBigButton from "@/components/buttons/NavigateBigButton";
import IconVotingInstructions from "../../components/icons/IconVotingInstructions.vue";
import ListPlaceholder from "@/components/list/ListPlaceholder";
import { ProgressStepId } from "@/utils/ProgressStepId";
import { ElectionProgressStepId } from "@/utils/ElectionProgressStepId";
import IconNotification from "@/components/icons/IconNotification";
import { ProgressState } from "@/utils/ProgressState";
import { DateUtility } from "@/utils/DateUtility";
import IsMobileMixin from "@/utils/IsMobileMixin";

export default {
  name: "DreamTeams",
  components: {
    IconNotification,
    ListPlaceholder,
    IconVotingInstructions,
    NavigateBigButton,
    BoxFilter,
    TutorialButton,
    NavigationTitleBox,
    TopSubB,
    BackButton,
    TopSmall,
    ContentScrollInner,
    ButtonGroup,
    Tutorial,
    ListChooseConfirmation,
    ContentScrollContainer,
    LayoutEnvelope,
    Navbar,
    CandidateList
  },
  mixins: [IsMobileMixin],
  async beforeRouteEnter(to, from, next) {
    if (!store.getters["user/hasProgress"](ProgressStepId.FavoriteList)) {
      next({ name: "election-home" });
      return;
    }

    await Promise.all([
      store.dispatch("election/fetchElections"),
      store.dispatch("candidate/fetchCandidates"),
      store.dispatch("candidate/fetchMatches"),
      store.dispatch("list/fetchChoice"),
      store.dispatch("list/fetchLists")
    ]);

    if (store.getters["election/activeElection"] == null) {
      next({ name: "location" });
      return;
    }

    next();
  },
  data: () => ({
    showChooseConfirmation: false,
    choosingNewList: true,
    chooseConfirmCallback: null,
    chooseConfirmBus: new Vue(),
    tutorialBus: new Vue(),
    changingElection: false
  }),
  computed: {
    ...mapGetters("user", ["user", "hasProgress"]),
    ...mapGetters("list", ["choice"]),
    ...mapGetters("election", [
      "election",
      "elections",
      "activeElection",
      "hasElectionProgress"
    ]),
    ...mapGetters("location", ["canton"]),
    background() {
      return this.canton(this.user.cantonId).backgroundGradient;
    },
    hasSeenResults() {
      return this.hasProgress(ProgressStepId.Results);
    },
    hasSeenDreamTeams() {
      return this.hasProgress(ProgressStepId.DreamTeams);
    },
    canSwitchCouncil() {
      return (
        this.hasSeenDreamTeams &&
        this.elections.length > 1 &&
        this.hasElectionProgress(
          ElectionProgressStepId.Match,
          this.elections[0].id
        )
      );
    }
  },
  created() {
    this.chooseConfirmBus.$on("choose", this.onShowChooseConfirmation);
  },
  mounted() {
    if (!this.hasSeenDreamTeams) {
      this.unlockAchievement();
    }
  },
  methods: {
    ...mapActions(["store", "get"]),
    ...mapActions("user", ["updateProgression"]),
    goHome(param) {
      this.store({ key: "homeMenuToggle", value: param });
      this.$router.push({ name: "election-home" });
    },
    unlockAchievement() {
      if (!this.hasSeenDreamTeams) {
        this.hasSeenDreamTeams = true;
        this.updateProgression({
          key: ProgressStepId.DreamTeams,
          data: { state: ProgressState.Completed }
        });
      }
    },
    onShowChooseConfirmation(event) {
      this.choosingNewList = event.listId !== this.choice;
      this.chooseConfirmCallback = confirm => {
        this.showChooseConfirmation = false;
        if (confirm && event.callback) event.callback();
      };
      this.showChooseConfirmation = true;
    },
    onBeforeElectionChange() {
      this.changingElection = true;
    },
    onElectionChanged() {
      this.changingElection = false;
    },
    formatDate(dateString) {
      return DateUtility.toLocaleDateString(new Date(dateString));
    }
  }
};
</script>

<style scoped>
.info-box {
  min-height: 17.40625rem;
}

@media screen and (min-width: 768px) {
  .info-box {
    min-height: 19.46875rem;
  }
}
</style>
